import React from 'react';

/**
 * Card 컴포넌트
 *
 * @param {string} title - 카드 제목
 * @param {string} subtitle - 카드 부제목
 * @param {React.ReactNode} action - 헤더 우측 액션 버튼
 * @param {React.ReactNode} children - 카드 내용
 * @param {string} className - 추가 클래스
 * @param {boolean} noPadding - 패딩 제거
 */
const Card = ({
  title,
  subtitle,
  action,
  children,
  className = '',
  noPadding = false
}) => {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${className}`}>
      {(title || action) && (
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <div className="flex-1">
            {title && <h3 className="text-[15px] font-semibold text-gray-900 m-0">{title}</h3>}
            {subtitle && <p className="text-[13px] text-gray-500 mt-1 mb-0">{subtitle}</p>}
          </div>
          {action && <div className="flex-shrink-0 ml-4">{action}</div>}
        </div>
      )}
      <div className={noPadding ? '' : 'p-5'}>
        {children}
      </div>
    </div>
  );
};

/**
 * StatCard 컴포넌트 - 통계용 카드
 *
 * @param {string} icon - 아이콘 (이모지)
 * @param {string|number} value - 값
 * @param {string} label - 라벨
 * @param {string} trend - 증감 추이 (+12%, -5% 등)
 * @param {boolean} clickable - 클릭 가능 여부
 * @param {function} onClick - 클릭 핸들러
 * @param {string} variant - warning, success, error
 */
export const StatCard = ({
  icon,
  value,
  label,
  trend,
  clickable = false,
  onClick,
  variant = ''
}) => {
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  const getValueColor = () => {
    switch (variant) {
      case 'warning': return 'text-amber-500';
      case 'success': return 'text-green-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-900';
    }
  };

  return (
    <div
      className={`flex items-center gap-4 p-5 bg-white border border-gray-200 rounded-xl transition-all duration-200
        ${clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg hover:border-blue-500 active:translate-y-0' : ''}`}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {icon && <span className="text-[28px] leading-none flex-shrink-0">{icon}</span>}
      <div className="flex flex-col gap-0.5 flex-1">
        <span className={`text-2xl font-bold leading-tight ${getValueColor()}`}>{value}</span>
        <span className="text-[13px] text-gray-500">{label}</span>
      </div>
      {trend && (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-[10px]
          ${trend.startsWith('+') ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
          {trend}
        </span>
      )}
    </div>
  );
};

/**
 * StatusCard 컴포넌트 - 상태 표시 카드
 *
 * @param {string} name - 서비스 이름
 * @param {string} status - online, offline, pending
 * @param {string} uptime - 가동 시간
 * @param {number} memory - 메모리 사용량 (MB)
 * @param {number} cpu - CPU 사용량 (%)
 * @param {function} onClick - 클릭 핸들러
 */
export const StatusCard = ({
  name,
  status = 'unknown',
  uptime,
  memory,
  cpu,
  onClick
}) => {
  const statusLabels = {
    online: '정상',
    offline: '오프라인',
    pending: '시작 중',
    error: '에러',
    unknown: '확인 중'
  };

  const getIndicatorColor = () => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'offline': return 'bg-red-500';
      case 'pending': return 'bg-amber-500 animate-pulse';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getBadgeStyle = () => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-600';
      case 'offline': return 'bg-red-100 text-red-600';
      case 'pending': return 'bg-amber-100 text-amber-600';
      case 'error': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  return (
    <div
      className="px-5 py-4 bg-white border border-gray-200 rounded-xl transition-all duration-150 cursor-pointer hover:border-gray-300 hover:shadow-sm"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center gap-2.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getIndicatorColor()}`}></span>
        <span className="text-sm font-semibold text-gray-900 flex-1">{name}</span>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-[10px] ${getBadgeStyle()}`}>
          {statusLabels[status] || status}
        </span>
      </div>
      {(uptime || memory !== undefined || cpu !== undefined) && (
        <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100">
          {uptime && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-gray-400">업타임</span>
              <span className="text-[13px] font-medium text-gray-900">{uptime}</span>
            </div>
          )}
          {memory !== undefined && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-gray-400">메모리</span>
              <span className="text-[13px] font-medium text-gray-900">{memory}MB</span>
            </div>
          )}
          {cpu !== undefined && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-gray-400">CPU</span>
              <span className="text-[13px] font-medium text-gray-900">{cpu}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Card;
